import { HttpResponse } from '@capacitor/core';
import {
  SESSION_ERROR_CODE,
  DATABASE_ERROR_CODE,
  SERVICE_ERROR_CODE,
  MOCKUP_ERROR_CODE,
  CLOUD_API_ERROR_CODE,
  ASWER_ERROR_CODE,
} from '../../../constants';
import {
  SessionError,
  DatabaseError,
  ApiError,
  AswerError,
  ServerError,
  ClientError,
  EntryError,
} from '../../errors';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('forceErrors');

export const processJSONResponseErrors = (
  response: HttpResponse,
  returnHeaders?: boolean,
  // Accumulated force value of the request that produced this response —
  // carried into a thrown EntryError so the next answer can be chained onto it.
  forceValue?: string
) => {
  if (response.status < 400) {
    if (returnHeaders) {
      const headers = {};
      Object.keys(response.headers).forEach((key) => {
        headers[key.toLowerCase()] = response.headers[key];
      });
      return {
        ...response,
        headers,
      };
    }
    return response.data;
  }

  if (response.data.error) {
    // Entry error (|E modifier) — takes precedence over the error code so the
    // user is asked for the value instead of seeing a plain error toast.
    if (response.data.error.entry) {
      /** @debugFeature forceErrors Logs an entry error on a non-449 error status — it pre-empts the error code the response also carried. */
      log('entry error detected on error status', () => ({
        status: response.status,
        url: response.url,
        preemptedErrorCode: response.data.error.code,
        entryForce: response.data.error.entry.force,
        entryType: response.data.error.entry.type,
        chained: Boolean(forceValue),
      }));

      const entryErrorData = {
        previousForce: forceValue,
        message: response.data.error.message,
        entry: response.data.error.entry,
      };
      throw new EntryError(JSON.stringify(entryErrorData));
    }

    switch (response.data.error.code) {
      case SESSION_ERROR_CODE:
        throw new SessionError(response.data.error.message);
      case DATABASE_ERROR_CODE:
        throw new DatabaseError(response.data.error.message);
      case SERVICE_ERROR_CODE:
      case MOCKUP_ERROR_CODE:
      case CLOUD_API_ERROR_CODE:
        throw new ApiError(response.data.error.message);
      case ASWER_ERROR_CODE:
        throw new AswerError(
          response.data.error.message,
          response.data.error.focusField
        );
      default:
        if (response.status >= 500) {
          throw new ServerError(response.data.error.message);
        }
        if (response.status >= 400) {
          throw new ClientError(
            response.data.error.message,
            response.data.error.code
          );
        }
    }
  }
  if (response.status >= 500) {
    throw new ServerError('Server error');
  }
  throw new ClientError('Fetch error');
};
